import { useState } from 'react';
import { X, Delete, Check } from 'lucide-react';

const PIN_MIN = 4;
const PIN_MAX = 8;

export default function ManagerPinDialog({ castka, onClose, onSubmit, isDark, title, subtitle }) {
    const headerTitle = title || 'PIN manažera';
    const headerSubtitle = subtitle ?? (castka != null ? `Vyplacení ${castka} Kč v hotovosti` : null);
    const [pin, setPin] = useState('');
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [shake, setShake] = useState(false);

    const press = (digit) => {
        if (submitting) return;
        if (pin.length >= PIN_MAX) return;
        setPin(pin + String(digit));
        setError(null);
    };

    const erase = () => {
        if (submitting) return;
        setPin(p => p.slice(0, -1));
        setError(null);
    };

    const submit = async () => {
        if (submitting) return;
        if (pin.length < PIN_MIN) return;
        setSubmitting(true);
        setError(null);
        const res = await onSubmit?.(pin);
        if (!res?.ok) {
            setShake(true);
            setError([401, 403].includes(res?.status) ? (res?.error || 'Neplatný PIN') : res?.status === 429 ? 'Příliš mnoho pokusů, zkuste později' : res?.error || 'Nepodařilo se potvrdit');
            setTimeout(() => { setPin(''); setShake(false); setSubmitting(false); }, 500);
        }
    };

    // Posledním tlačítkem je OK (potvrzení) místo del — del posunut na první pozici
    // posledního řádku, aby uživatel intuitivně potvrzoval doprava jako u terminálu.
    const keys = [['1','2','3'], ['4','5','6'], ['7','8','9'], ['del', '0', 'ok']];
    const canSubmit = pin.length >= PIN_MIN && !submitting;

    return (
        <div onClick={onClose} style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
            <div onClick={e => e.stopPropagation()} style={{
                width: '100%', maxWidth: 360,
                background: isDark ? '#1a1a1a' : 'var(--surface-elevated)',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'var(--border-default)'}`,
                borderRadius: 14, padding: 20,
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                    <div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8 }}>{headerTitle}</div>
                        {headerSubtitle && (
                            <div style={{ fontSize: 15, fontWeight: 700, marginTop: 2, color: isDark ? '#fff' : 'var(--text-primary)' }}>
                                {headerSubtitle}
                            </div>
                        )}
                    </div>
                    <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                        <X size={20} />
                    </button>
                </div>

                <div className={shake ? 'ms-shake' : ''} style={{ display: 'flex', justifyContent: 'center', gap: 10, padding: '16px 0' }}>
                    {/* Zobrazujeme min PIN_MIN teček, dál až do PIN_MAX přidáváme dle psaní */}
                    {Array.from({ length: Math.max(PIN_MIN, pin.length) }).map((_, i) => {
                        const filled = i < pin.length;
                        return (
                            <span key={i} style={{
                                width: 14, height: 14, borderRadius: '50%',
                                background: filled ? (error ? '#EF4444' : '#10B981') : 'transparent',
                                border: `2px solid ${filled ? (error ? '#EF4444' : '#10B981') : isDark ? 'rgba(255,255,255,0.25)' : 'var(--border-default)'}`,
                                transition: 'all 0.15s',
                            }} />
                        );
                    })}
                </div>

                {error && (
                    <div style={{ textAlign: 'center', fontSize: 12, color: '#EF4444', marginBottom: 10 }}>{error}</div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                    {keys.flat().map((k, i) => {
                        if (k === '') return <span key={i} />;
                        if (k === 'del') return (
                            <button key={i} type="button" onClick={erase} disabled={submitting || pin.length === 0} style={{
                                padding: '16px 0', borderRadius: 10,
                                background: isDark ? 'rgba(255,255,255,0.05)' : 'var(--surface-raised)',
                                border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}`,
                                color: isDark ? 'rgba(255,255,255,0.7)' : 'var(--text-secondary)',
                                fontSize: 18, cursor: pin.length === 0 ? 'default' : 'pointer',
                                opacity: pin.length === 0 ? 0.4 : 1,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <Delete size={18} />
                            </button>
                        );
                        if (k === 'ok') return (
                            <button key={i} type="button" onClick={submit} disabled={!canSubmit} style={{
                                padding: '16px 0', borderRadius: 10,
                                background: canSubmit ? '#10B981' : (isDark ? 'rgba(255,255,255,0.05)' : 'var(--surface-raised)'),
                                border: canSubmit ? '1px solid #10B981' : `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}`,
                                color: canSubmit ? '#fff' : (isDark ? 'rgba(255,255,255,0.4)' : 'var(--text-muted)'),
                                fontSize: 18, cursor: canSubmit ? 'pointer' : 'default',
                                opacity: canSubmit ? 1 : 0.5,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'background 0.12s, color 0.12s',
                            }}>
                                <Check size={20} strokeWidth={2.5} />
                            </button>
                        );
                        return (
                            <button key={i} type="button" onClick={() => press(k)} disabled={submitting || pin.length >= PIN_MAX} style={{
                                padding: '16px 0', borderRadius: 10,
                                background: isDark ? 'rgba(255,255,255,0.05)' : 'var(--surface-raised)',
                                border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}`,
                                color: isDark ? '#fff' : 'var(--text-primary)',
                                fontSize: 22, fontWeight: 600,
                                cursor: submitting ? 'wait' : (pin.length >= PIN_MAX ? 'default' : 'pointer'),
                                opacity: pin.length >= PIN_MAX ? 0.5 : 1,
                                fontVariantNumeric: 'tabular-nums',
                            }}>
                                {k}
                            </button>
                        );
                    })}
                </div>

                <button type="button" onClick={onClose} style={{ marginTop: 14, width: '100%', padding: '10px', borderRadius: 7, background: 'transparent', color: 'var(--text-muted)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'var(--border-default)'}`, fontSize: 13, cursor: 'pointer' }}>
                    Zrušit
                </button>
            </div>
        </div>
    );
}
